import {
	autoinject,
	bindable,
	bindingMode,
	computedFrom,
	customElement,
	PLATFORM,
	useView,
} from 'aurelia-framework';

import { UserModel } from '~/api/user-service';
import { UserField } from '~/components/field/custom/user';
import { DateFormatValueConverter } from '~/converters/date-format';
import {
	commonFieldProperties,
	registerComponent,
} from '~/forms/customisation-editor/property-meta';
import { FieldModel } from '~/models/form-model';
import { LocalizedDateTimeFormats } from '~/utils/datetime/localized-date-time-formats';

const componentTagName = 'a-modified-by';

const styles = {
	body: 'a-modifiedby__body',
	label: 'a-modifiedby__label',
	value: 'a-modifiedby__value',
	row: 'a-modifiedby__row',
	tooltip: 'a-modifiedby__tooltip',
	tooltipInner: 'a-modifiedby__tooltip__inner',
	hide: 'a-modifiedby__hide',
};

registerComponent(componentTagName, {
	name: 'Modified By',
	requirePath: PLATFORM.moduleName('components/field/custom/modified-by'),
	props: [
		...commonFieldProperties,
		{
			label: 'Modified Timestamp Model',
			name: 'modifiedTimestamp',
			type: 'FieldModel',
		},
	],
	isSelectable: true,
});

@autoinject
@customElement(componentTagName)
@useView(PLATFORM.moduleName('./modified-by.html'))
export class ModifiedBy extends UserField {
	@bindable({
		defaultBindingMode: bindingMode.twoWay,
	})
	public modifiedTimestamp: FieldModel;

	/**
	 * Check if the modified field should be shown or not. This allows us to easily hide
	 * modified fields for 'singleRecordDisplay' templates when we're in the "Add Record" view. (ie. new record)
	 */
	@computedFrom(
		'fieldModel',
		'fieldModel.value',
		'value',
		'modifiedTimestamp',
		'modifiedTimestamp.value',
	)
	get shouldDisplayField(): boolean {
		return Boolean(
			this.fieldModel &&
				this.modifiedTimestamp &&
				(this.fieldModel.value || this.modifiedTimestamp.value),
		);
	}

	/**
	 * Compute the display value.
	 * Used when the field is in non-edit mode.
	 */
	@computedFrom(
		'fieldModel',
		'fieldModel.value',
		'value',
		'modifiedTimestamp',
		'modifiedTimestamp.value',
		'userModel',
	)
	get displayValue(): string {
		if (
			!this.fieldModel ||
			!this.modifiedTimestamp ||
			!this.fieldModel.value ||
			!this.modifiedTimestamp.value
		) {
			return '';
		}
		if (this.isInitialized) {
			$(this.refTooltipEl).tooltip('update');
		}
		let result = '';
		if (this.shouldDisplayModTime()) {
			result = DateFormatValueConverter.toViewStatic(
				this.modifiedTimestamp.value,
				LocalizedDateTimeFormats.DateTime,
				true,
			);
		} else if (this.shouldDisplayModDate()) {
			result = DateFormatValueConverter.toViewStatic(
				this.modifiedTimestamp.value,
				LocalizedDateTimeFormats.Date,
				true,
			);
		}
		if (
			!this.isLoginNameInvisible &&
			this.userModel &&
			this.userModel.userLoginName
		) {
			result +=
				result.length > 0
					? ' - ' + this.userModel.userLoginName
					: this.userModel.userLoginName;
		}
		return result;
	}

	private hasModifiedTimestampValue(): boolean {
		return (
			this.modifiedTimestamp.value &&
			this.modifiedTimestamp.value.length > 0
		);
	}

	private shouldDisplayModDate(): boolean {
		return (
			this.hasModifiedTimestampValue() &&
			(this.modifiedTimestamp.dataType.name === 'Date' ||
				this.modifiedTimestamp.dataType.name === 'Time' ||
				this.modifiedTimestamp.dataType.name === 'Timestamp')
		);
	}

	private shouldDisplayModTime(): boolean {
		return (
			this.hasModifiedTimestampValue() &&
			(this.modifiedTimestamp.dataType.name === 'Time' ||
				this.modifiedTimestamp.dataType.name === 'Timestamp')
		);
	}

	protected getTooltipContent(userModel: UserModel) {
		return `
		<ul class="${styles.body}">
			<li class="${styles.row}">
				<span class="${styles.label}">
					Identity
				</span>
				<div class="${styles.value}">
					${userModel.id.escapeHtml()}
				</div>
			</li>
			<li class="${styles.row} ${this.shouldDisplayModDate() ? '' : styles.hide}">
				<span class="${styles.label}">
					Mod Date
				</span>
				<div class="${styles.value}">
					${DateFormatValueConverter.toViewStatic(
						this.modifiedTimestamp.value,
						LocalizedDateTimeFormats.Date,
						true,
					).escapeHtml()}
				</div>
			</li>
			<li class="${styles.row} ${this.shouldDisplayModTime() ? '' : styles.hide}">
				<span class="${styles.label}">
					Mod Time
				</span>
				<div class="${styles.value}">
					${DateFormatValueConverter.toViewStatic(
						this.modifiedTimestamp.value,
						LocalizedDateTimeFormats.DateTime,
						true,
					).escapeHtml()}
				</div>
			</li>
			<li class="${styles.row}">
				<span class="${styles.label}">
					Web User
				</span>
				<div class="${styles.value}">
					${userModel.userLoginName.escapeHtml()}
				</div>
			</li>
			<li class="${styles.row}">
				<span class="${styles.label}">
					Surname
				</span>
				<div class="${styles.value}">
					${userModel.surname.escapeHtml()}
				</div>
			</li>
			<li class="${styles.row}">
				<span class="${styles.label}">
					Given Name
				</span>
				<div class="${styles.value}">
					${userModel.givenName.escapeHtml()}
				</div>
			</li>
		</ul>
		`;
	}
}
